import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query, Response } from '../op-types';

export function createAdminQueries(client: StoxlyClient) {
  return createQueryKeys('admin', {
    // ['admin', 'backfillList']
    backfillList: {
      queryKey: null,
      queryFn: ({ signal }) => client.backfillList({ signal }),
    },

    // ['admin', 'backfillListCampaigns']
    backfillListCampaigns: {
      queryKey: null,
      queryFn: ({ signal }) => client.backfillListCampaigns({ signal }),
    },

    // ['admin', 'backfillCampaignStatus', id]
    backfillCampaignStatus: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.backfillCampaignStatus({ params: { id }, signal }),
    }),

    // ['admin', 'backfillPreviewForeignAdrs', { query }]
    backfillPreviewForeignAdrs: (query: Query<'backfillPreviewForeignAdrs'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.backfillPreviewForeignAdrs({ queries: query, signal }),
    }),

    // ['admin', 'backfillStatus', jobId]
    backfillStatus: (jobId: string) => ({
      queryKey: [jobId],
      queryFn: ({ signal }) => client.backfillStatus({ params: { jobId }, signal }),
    }),

    // ['admin', 'bulkSyncStatus']
    bulkSyncStatus: {
      queryKey: null,
      queryFn: ({ signal }) => client.bulkSyncGetStatus({ signal }),
    },

    // ['admin', 'billingCatalog']
    billingCatalog: {
      queryKey: null,
      queryFn: ({ signal }) => client.billingGetAdminCatalog({ signal }),
    },

    // ['admin', 'billingEntitlements']
    billingEntitlements: {
      queryKey: null,
      queryFn: ({ signal }) => client.billingGetAdminEntitlements({ signal }),
    },

    // ['admin', 'billingStripeProducts', query]
    billingStripeProducts: (query: Query<'billingBrowseStripeProducts'>) => ({
      queryKey: [query],
      queryFn: ({ signal }) => client.billingBrowseStripeProducts({ queries: query, signal }),
    }),

    // ['admin', 'billingStripeProductPrices', id]
    billingStripeProductPrices: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.billingBrowseStripePrices({ params: { id }, signal }),
    }),

    // ['admin', 'billingStripePricePreview', id]
    billingStripePricePreview: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.billingPreviewStripePrice({ params: { id }, signal }),
    }),

    // ['admin', 'allUsersXlsxDownload']
    allUsersXlsxDownload: {
      queryKey: null,
      queryFn: ({ signal }) => client.adminDownloadAllUsersXlsx({ signal }),
    },

    // ['admin', 'importedFileDownloadUrl', fileId]
    importedFileDownloadUrl: (fileId: string) => ({
      queryKey: [fileId],
      queryFn: ({ signal }) =>
        client.adminGetImportedFileDownloadUrl({ params: { fileId }, signal }),
    }),

    // ['admin', 'snaptradeResponseLogs', { query }]
    snaptradeResponseLogs: (query: Query<'adminGetSnaptradeResponseLogs'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.adminGetSnaptradeResponseLogs({ queries: query, signal }),
    }),

    // ['admin', 'snaptradeRunDownload', runId]
    snaptradeRunDownload: (runId: string) => ({
      queryKey: [runId],
      queryFn: ({ signal }) => client.adminGetSnaptradeRunDownload({ params: { runId }, signal }),
    }),

    // ['admin', 'snaptradeLogDownloadUrl', logId]
    snaptradeLogDownloadUrl: (logId: string) => ({
      queryKey: [logId],
      queryFn: ({ signal }) =>
        client.adminGetSnaptradeLogDownloadUrl({ params: { logId }, signal }),
    }),

    // ['admin', 'snaptradeWebhookLogs', { query }]
    snaptradeWebhookLogs: (query: Query<'adminGetSnaptradeWebhookLogs'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.adminGetSnaptradeWebhookLogs({ queries: query, signal }),
    }),

    // ['admin', 'dashboardStats']
    dashboardStats: {
      queryKey: null,
      queryFn: ({ signal }) => client.adminGetDashboardStats({ signal }),
    },

    // ['admin', 'users', { query }]
    users: (query?: Query<'adminGetUsers'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.adminGetUsers({ queries: query, signal }),
    }),

    // ['admin', 'usersHealth', { query }] — the users-list health column. Keyed by the id
    // list, so paging to the next page is a different key and not a refetch of this one.
    usersHealth: (query: Query<'adminUserHealthGetUsersHealth'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.adminUserHealthGetUsersHealth({ queries: query, signal }),
    }),

    // ['admin', 'deletionFeedback', { query }] — anonymous exit-survey answers.
    deletionFeedback: (query?: Query<'adminGetDeletionFeedback'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.adminGetDeletionFeedback({ queries: query, signal }),
    }),

    // ['admin', 'broadcasts'] — every broadcast, newest first.
    broadcasts: {
      queryKey: null,
      queryFn: ({ signal }) => client.broadcastsAdminList({ signal }),
    },

    // ['admin', 'broadcast', id]
    broadcast: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.broadcastsAdminGet({ params: { id }, signal }),
    }),

    // ['admin', 'broadcastSentHtml', id, language] — the email one language of a send received.
    broadcastSentHtml: (
      id: string,
      language: Response<'broadcastsAdminGet'>['languages'][number]['language'],
    ) => ({
      queryKey: [id, language],
      queryFn: ({ signal }) => client.broadcastsAdminSentHtml({ params: { id, language }, signal }),
    }),

    // ['admin', 'broadcastAudience'] — recipients per audience and language.
    broadcastAudience: {
      queryKey: null,
      queryFn: ({ signal }) => client.broadcastsAdminAudience({ signal }),
    },

    // ['admin', 'broadcastSenders'] — the default sender, earlier senders, the sending domains.
    broadcastSenders: {
      queryKey: null,
      queryFn: ({ signal }) => client.broadcastsAdminSenders({ signal }),
    },

    // ['admin', 'broadcastTemplates'] — the email templates and the placeholders they may use.
    broadcastTemplates: {
      queryKey: null,
      queryFn: ({ signal }) => client.broadcastTemplatesAdminList({ signal }),
    },

    // ['admin', 'broadcastSettings'] — the translation model and the models to pick from.
    broadcastSettings: {
      queryKey: null,
      queryFn: ({ signal }) => client.broadcastsAdminSettings({ signal }),
    },

    // ['admin', 'broadcastPreview', { body }] — a POST, but a pure render: keyed by the text it
    // renders, so the composer's live preview caches and dedupes like any read.
    broadcastPreview: (body: Body<'broadcastsAdminPreview'>) => ({
      queryKey: [{ body }],
      queryFn: ({ signal }) => client.broadcastsAdminPreview(body, { signal }),
    }),

    // ['admin', 'searchEngines'] — the engines behind the copilot's web_search, in order.
    searchEngines: {
      queryKey: null,
      queryFn: ({ signal }) => client.searchEnginesAdminList({ signal }),
    },

    // ['admin', 'recentSearches', { query }] — the copilot's searches, every engine asked.
    recentSearches: (query: Query<'searchEnginesAdminRecent'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.searchEnginesAdminRecent({ queries: query, signal }),
    }),

    // ['admin', 'importUnknownLabels'] — statement labels no parser rule knows, ranked.
    importUnknownLabels: {
      queryKey: null,
      queryFn: ({ signal }) => client.importUnknownLabelsAdminList({ signal }),
    },

    // ['admin', 'detail', userId, ...]
    detail: (userId: string) => ({
      queryKey: [userId],
      queryFn: ({ signal }) => client.adminGetUserById({ params: { id: userId }, signal }),
      contextQueries: {
        // Subscription history (DB) + payment history (live Stripe read — nothing persists
        // invoices locally).
        billing: {
          queryKey: null,
          queryFn: ({ signal }) => client.adminGetUserBilling({ params: { id: userId }, signal }),
        },
        activity: {
          queryKey: null,
          queryFn: ({ signal }) => client.adminGetUserActivity({ params: { id: userId }, signal }),
        },
        importedFiles: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.adminGetUserImportedFiles({ params: { id: userId }, signal }),
        },
        snaptradeBundleDownload: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.adminDownloadUserSnaptradeBundle({ params: { id: userId }, signal }),
        },
        // The compensation apology, rendered in the user's own language. Keyed by the day
        // count because the copy PRINTS it — a render cached across counts would quote a
        // number of free days the admin did not choose.
        apologyTemplate: (query: Query<'adminGetApologyTemplate'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.adminGetApologyTemplate({ params: { id: userId }, queries: query, signal }),
        }),
        snaptradeResponseLogs: (query: Query<'adminGetUserSnaptradeResponseLogs'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.adminGetUserSnaptradeResponseLogs({
              params: { id: userId },
              queries: query,
              signal,
            }),
        }),
      },
    }),
  });
}

export function createAdminMutations(client: StoxlyClient) {
  return {
    backfillTrigger: {
      mutationFn: (body: Body<'backfillTrigger'>) => client.backfillTrigger(body),
    },
    backfillTriggerCampaign: {
      mutationFn: (body: Body<'backfillTriggerCampaign'>) => client.backfillTriggerCampaign(body),
    },
    backfillStopCampaign: {
      mutationFn: (id: string) => client.backfillStopCampaign(undefined, { params: { id } }),
    },
    bulkSyncClearJob: {
      mutationFn: (query: Query<'bulkSyncClearJob'>) =>
        client.bulkSyncClearJob(undefined, { queries: query }),
    },
    bulkSyncTriggerSync: {
      mutationFn: (query: Query<'bulkSyncTriggerSync'>) =>
        client.bulkSyncTriggerSync(undefined, { queries: query }),
    },
    bulkSyncTriggerAll: {
      mutationFn: () => client.bulkSyncTriggerAll(undefined),
    },
    // §4.1: the bounded non-positive-year-end-Q4 recovery pass (supervised manual run;
    // works regardless of Q4_RECOVERY_ENABLED). Surfaced by the step-3 openapi regen —
    // the endpoint landed on the backend without its spec artifacts.
    bulkSyncTriggerYearEndRecovery: {
      mutationFn: (query: Query<'bulkSyncTriggerYearEndRecovery'>) =>
        client.bulkSyncTriggerYearEndRecovery(undefined, { queries: query }),
    },
    impersonateUser: {
      mutationFn: (id: string) => client.adminImpersonateUser(undefined, { params: { id } }),
    },
    updateUserRole: {
      mutationFn: ({ id, body }: { id: string; body: Body<'adminUpdateUserRole'> }) =>
        client.adminUpdateUserRole(body, { params: { id } }),
    },
    // Grant/revoke a plan tier without Stripe. `body.planTier: null` REVOKES — it is a value
    // on the wire, never an omission (the DTO 400s on a missing field for exactly that reason).
    updateUserPlanOverride: {
      mutationFn: ({ id, body }: { id: string; body: Body<'adminUpdateUserPlanOverride'> }) =>
        client.adminUpdateUserPlanOverride(body, { params: { id } }),
    },
    // PERMANENT account deletion (admin-initiated; no exit survey, no subscription gate —
    // any Stripe subscription must be cancelled manually in the Stripe dashboard).
    deleteUser: {
      mutationFn: (id: string) => client.adminDeleteUser(undefined, { params: { id } }),
    },
    triggerSnaptradeSync: {
      mutationFn: (id: string) => client.adminTriggerSnaptradeSync(undefined, { params: { id } }),
    },
    // #1660: re-pull the user's broker connections from SnapTrade (the sync above only
    // re-reads holdings of connections already known). Surfaced by the spine coverage
    // test — the endpoint landed on the backend without a domain wrapper.
    triggerSnaptradeBrokerRefresh: {
      mutationFn: (id: string) =>
        client.adminTriggerSnaptradeBrokerRefresh(undefined, { params: { id } }),
    },
    billingRefreshCatalog: {
      mutationFn: () => client.billingRefreshCatalog(undefined),
    },
    // Re-read every subscription from Stripe (status, discounts, ended_at) into the local rows —
    // the backfill + repair action behind the admin Subscriptions page's MRR.
    adminAnalyticsResyncSubscriptions: {
      mutationFn: () => client.adminAnalyticsResyncSubscriptions(undefined),
    },
    // Stamp the Early Bird cutoff at now — once; the server answers 409 on any later press.
    adminAnalyticsSetEarlyBirdCutoff: {
      mutationFn: () => client.adminAnalyticsSetEarlyBirdCutoff(undefined),
    },
    // The Stripe portal configuration Early Bird sessions run on: set (validated on Stripe and
    // written at once), clear (back to the default portal), or rewrite its list from the catalog.
    adminAnalyticsSetEarlyBirdPortalConfiguration: {
      mutationFn: (body: Body<'adminAnalyticsSetEarlyBirdPortalConfiguration'>) =>
        client.adminAnalyticsSetEarlyBirdPortalConfiguration(body),
    },
    adminAnalyticsClearEarlyBirdPortalConfiguration: {
      mutationFn: () => client.adminAnalyticsClearEarlyBirdPortalConfiguration(undefined),
    },
    adminAnalyticsSyncEarlyBirdPortal: {
      mutationFn: () => client.adminAnalyticsSyncEarlyBirdPortal(undefined),
    },
    billingCreateProduct: {
      mutationFn: (body: Body<'billingCreateProduct'>) => client.billingCreateProduct(body),
    },
    billingUpdateProduct: {
      mutationFn: ({ id, body }: { id: string; body: Body<'billingUpdateProduct'> }) =>
        client.billingUpdateProduct(body, { params: { id } }),
    },
    billingDeleteProduct: {
      mutationFn: (id: string) => client.billingDeleteProduct(undefined, { params: { id } }),
    },
    billingAssignPrice: {
      mutationFn: (body: Body<'billingAssignPrice'>) => client.billingAssignPrice(body),
    },
    billingSetCurrentPrice: {
      mutationFn: (id: string) => client.billingSetCurrentPrice(undefined, { params: { id } }),
    },
    // The early-bird (launch price) slot: mark / clear. No swap — the service 409s a taken slot.
    billingSetEarlyBirdPrice: {
      mutationFn: (id: string) => client.billingSetEarlyBirdPrice(undefined, { params: { id } }),
    },
    billingClearEarlyBirdPrice: {
      mutationFn: (id: string) => client.billingClearEarlyBirdPrice(undefined, { params: { id } }),
    },
    // Move a price to another plan's product (the wrong-plan repair); it arrives non-current.
    billingReassignPrice: {
      mutationFn: ({ id, body }: { id: string; body: Body<'billingReassignPrice'> }) =>
        client.billingReassignPrice(body, { params: { id } }),
    },
    billingArchivePrice: {
      mutationFn: (id: string) => client.billingArchivePrice(undefined, { params: { id } }),
    },
    billingUnarchivePrice: {
      mutationFn: (id: string) => client.billingUnarchivePrice(undefined, { params: { id } }),
    },
    billingClearPriceReview: {
      mutationFn: (id: string) => client.billingClearPriceReview(undefined, { params: { id } }),
    },
    billingDeletePrice: {
      mutationFn: (id: string) => client.billingDeletePrice(undefined, { params: { id } }),
    },
    billingSyncEntitlements: {
      mutationFn: () => client.billingSyncEntitlements(undefined),
    },
    billingUpdateEntitlement: {
      mutationFn: (body: Body<'billingUpdateEntitlement'>) => client.billingUpdateEntitlement(body),
    },
    createBroadcast: {
      mutationFn: (body: Body<'broadcastsAdminCreate'>) => client.broadcastsAdminCreate(body),
    },
    updateBroadcast: {
      mutationFn: ({ id, body }: { id: string; body: Body<'broadcastsAdminUpdate'> }) =>
        client.broadcastsAdminUpdate(body, { params: { id } }),
    },
    deleteBroadcast: {
      mutationFn: (id: string) => client.broadcastsAdminRemove(undefined, { params: { id } }),
    },
    testBroadcast: {
      mutationFn: ({ id, body }: { id: string; body: Body<'broadcastsAdminSendTest'> }) =>
        client.broadcastsAdminSendTest(body, { params: { id } }),
    },
    translateBroadcast: {
      mutationFn: ({ id, body }: { id: string; body: Body<'broadcastsAdminTranslate'> }) =>
        client.broadcastsAdminTranslate(body, { params: { id } }),
    },
    sendBroadcast: {
      mutationFn: ({ id, body }: { id: string; body: Body<'broadcastsAdminSend'> }) =>
        client.broadcastsAdminSend(body, { params: { id } }),
    },
    cancelBroadcast: {
      mutationFn: (id: string) => client.broadcastsAdminCancel(undefined, { params: { id } }),
    },
    retryBroadcast: {
      mutationFn: (id: string) => client.broadcastsAdminRetry(undefined, { params: { id } }),
    },
    uploadBroadcastImage: {
      mutationFn: (file: File) => client.broadcastsAdminUploadImage({ file }),
    },
    createBroadcastTemplate: {
      mutationFn: (body: Body<'broadcastTemplatesAdminCreate'>) =>
        client.broadcastTemplatesAdminCreate(body),
    },
    updateBroadcastTemplate: {
      mutationFn: ({ id, body }: { id: string; body: Body<'broadcastTemplatesAdminUpdate'> }) =>
        client.broadcastTemplatesAdminUpdate(body, { params: { templateId: id } }),
    },
    deleteBroadcastTemplate: {
      mutationFn: (id: string) =>
        client.broadcastTemplatesAdminRemove(undefined, { params: { templateId: id } }),
    },
    createSearchEngine: {
      mutationFn: (body: Body<'searchEnginesAdminCreate'>) => client.searchEnginesAdminCreate(body),
    },
    updateSearchEngine: {
      mutationFn: ({ id, body }: { id: string; body: Body<'searchEnginesAdminUpdate'> }) =>
        client.searchEnginesAdminUpdate(body, { params: { engineId: id } }),
    },
    deleteSearchEngine: {
      mutationFn: (id: string) =>
        client.searchEnginesAdminRemove(undefined, { params: { engineId: id } }),
    },
    reorderSearchEngines: {
      mutationFn: (body: Body<'searchEnginesAdminReorder'>) =>
        client.searchEnginesAdminReorder(body),
    },
    // Not cached: each Test and each tried search is a fresh, real request to the engines.
    testSearchEngine: {
      mutationFn: (body: Body<'searchEnginesAdminTest'>) => client.searchEnginesAdminTest(body),
    },
    trySearch: {
      mutationFn: (body: Body<'searchEnginesAdminTrySearch'>) =>
        client.searchEnginesAdminTrySearch(body),
    },
    updateBroadcastSettings: {
      mutationFn: (body: Body<'broadcastsAdminUpdateSettings'>) =>
        client.broadcastsAdminUpdateSettings(body),
    },
    setImportUnknownLabelStatus: {
      mutationFn: ({ id, body }: { id: string; body: Body<'importUnknownLabelsAdminSetStatus'> }) =>
        client.importUnknownLabelsAdminSetStatus(body, { params: { id } }),
    },
  };
}
